package er.prateek.crossly.ui.coach

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.Modifier

data class CoachTooltipPlacement(
    val origin: Offset,
    /** True when the card sits below the hole, so the caret lives on the card's top edge. */
    val caretOnTop: Boolean,
    /** Caret tip X, relative to the card's left edge. */
    val caretX: Float,
    val showCaret: Boolean,
)

fun Modifier.reportCoachBounds(onBounds: (Rect) -> Unit): Modifier =
    onGloballyPositioned { onBounds(it.boundsInRoot()) }

fun Rect.toOverlayLocal(origin: Offset): Rect = translate(-origin.x, -origin.y)

/**
 * Places the coach card next to [hole] without covering it, preferring below
 * then above, and clamping so a landscape phone or a short desktop window never
 * pushes the card off-screen.
 */
fun placeCoachTooltip(
    overlay: Size,
    hole: Rect,
    cardWidth: Float,
    cardHeight: Float,
    gap: Float,
    margin: Float,
): CoachTooltipPlacement {
    val maxX = (overlay.width - margin - cardWidth).coerceAtLeast(margin)
    val maxY = (overlay.height - margin - cardHeight).coerceAtLeast(margin)

    if (hole.isEmpty || overlay.width <= 0f || overlay.height <= 0f) {
        return CoachTooltipPlacement(
            origin = Offset(
                x = ((overlay.width - cardWidth) / 2f).coerceIn(margin, maxX),
                y = ((overlay.height - cardHeight) / 2f).coerceIn(margin, maxY),
            ),
            caretOnTop = false,
            caretX = cardWidth / 2f,
            showCaret = false,
        )
    }

    val fitsBelow = hole.bottom + gap + cardHeight + margin <= overlay.height
    val fitsAbove = hole.top - gap - cardHeight >= margin
    val spaceBelow = overlay.height - hole.bottom
    val spaceAbove = hole.top

    val placeBelow = when {
        fitsBelow -> true
        fitsAbove -> false
        else -> spaceBelow >= spaceAbove
    }

    val y = if (placeBelow) {
        (hole.bottom + gap).coerceIn(margin, maxY)
    } else {
        (hole.top - gap - cardHeight).coerceIn(margin, maxY)
    }
    val x = (hole.center.x - cardWidth / 2f).coerceIn(margin, maxX)
    val caretPad = 18f
    val caretX = (hole.center.x - x).coerceIn(caretPad, (cardWidth - caretPad).coerceAtLeast(caretPad))

    return CoachTooltipPlacement(
        origin = Offset(x, y),
        caretOnTop = placeBelow,
        caretX = caretX,
        showCaret = true,
    )
}
